import React, { useEffect, useMemo, useState } from "react"
import { Alert, Button, Col, Form, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap"
import moment from "moment"
import { toast } from "react-toastify"
import { get, post, put } from "helpers/api_helper"
import { formatDate, formatINR, formatMobile } from "helpers/common_helper"

const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card", label: "Card" },
  { value: "complimentary", label: "Complimentary (Free)" },
  { value: "other", label: "Other" },
]

const errorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.error || fallback

const emptyForm = () => ({
  packageId: "",
  amount: "",
  paymentMode: "",
  referenceNo: "",
  note: "",
})

function AddSubscriptionModal({ isOpen, subscriptionId, toggle, onSaved }) {
  const isEdit = !!subscriptionId

  const [mobile, setMobile] = useState("")
  const [finding, setFinding] = useState(false)
  const [customer, setCustomer] = useState(null)
  const [activeSubscription, setActiveSubscription] = useState(null)
  const [lookupError, setLookupError] = useState("")

  const [packages, setPackages] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  // edit mode
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [editing, setEditing] = useState(null) // the subscription being edited

  // Fresh modal every time it opens.
  useEffect(() => {
    if (!isOpen) return
    let active = true
    setMobile("")
    setCustomer(null)
    setActiveSubscription(null)
    setLookupError("")
    setForm(emptyForm())
    setEditing(null)

    if (!isEdit) {
      post("packages/search", { status: true })
        .then(data => active && setPackages(Array.isArray(data) ? data : []))
        .catch(err => toast.error(errorMessage(err, "Could not load packages")))
      return () => {
        active = false
      }
    }

    setLoadingEdit(true)
    Promise.all([get(`subscriptions/${subscriptionId}`), post("packages/search", {})])
      .then(([sub, pkgs]) => {
        if (!active) return
        setEditing(sub)
        // enabled packages + the current one (even if disabled since)
        setPackages((Array.isArray(pkgs) ? pkgs : []).filter(p => p.status || p._id === sub?.package?._id))
        setCustomer({ ...(sub?.customer || {}), city: sub?.city })
        setMobile(String(sub?.customer?.mobile || "").replace(/\D/g, "").slice(-10))
        setForm({
          packageId: sub?.package?._id || "",
          amount: String(Number(sub?.amount || 0) - Number(sub?.discount || 0)),
          paymentMode: sub?.paymentDetails?.method || "",
          referenceNo: sub?.paymentDetails?.referenceNo || "",
          note: sub?.note || "",
        })
      })
      .catch(err => {
        if (!active) return
        toast.error(errorMessage(err, "Could not load subscription"))
        toggle()
      })
      .finally(() => active && setLoadingEdit(false))
    return () => {
      active = false
    }
  }, [isOpen, subscriptionId])

  const isManual = !isEdit || editing?.source === "admin"
  const usedTurns = Number(editing?.usedTurns || 0)

  const selectedPackage = useMemo(
    () => packages.find(p => p._id === form.packageId) || null,
    [packages, form.packageId],
  )

  // Add: starts today; ends after the package validity (1 year for annual plans).
  const validTill = selectedPackage
    ? moment().add(Number(selectedPackage.validity) || 12, "months")
    : null

  const findCustomer = async () => {
    const digits = mobile.replace(/\D/g, "")
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setLookupError("Enter a valid 10-digit mobile number")
      return
    }
    setFinding(true)
    setLookupError("")
    setCustomer(null)
    setActiveSubscription(null)
    try {
      const data = await post("subscriptions/customer-by-mobile", { mobile: digits })
      setCustomer(data?.customer || null)
      setActiveSubscription(data?.activeSubscription || null)
    } catch (err) {
      setLookupError(errorMessage(err, "Could not find the customer"))
    } finally {
      setFinding(false)
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setForm(prev => {
      const next = { ...prev, [name]: value }
      if (name === "packageId") {
        const pkg = packages.find(p => p._id === value)
        // picking a package fills its price as the amount (admin can change it)
        if (!isEdit || value !== editing?.package?._id) next.amount = pkg ? String(pkg.price) : ""
      }
      // Complimentary = nothing received.
      if (name === "paymentMode" && value === "complimentary") next.amount = "0"
      return next
    })
  }

  const canSave = isEdit
    ? !!form.packageId && form.amount !== "" && !saving
    : !!customer && !activeSubscription && !!form.packageId && !!form.paymentMode && form.amount !== "" && !saving

  const handleSave = async e => {
    e.preventDefault()
    if (!canSave) return
    setSaving(true)
    try {
      if (isEdit) {
        await put(`subscriptions/${subscriptionId}`, {
          packageId: form.packageId,
          amount: Number(form.amount),
          note: form.note,
          referenceNo: form.referenceNo,
          ...(form.paymentMode ? { paymentMode: form.paymentMode } : {}),
        })
        toast.success("Subscription updated")
      } else {
        await post("subscriptions/manual", {
          customerId: customer._id,
          packageId: form.packageId,
          amount: Number(form.amount),
          paymentMode: form.paymentMode,
          referenceNo: form.referenceNo,
          note: form.note,
        })
        toast.success("Subscription added")
      }
      onSaved?.()
      toggle()
    } catch (err) {
      toast.error(errorMessage(err, isEdit ? "Could not update subscription" : "Could not add subscription"))
    } finally {
      setSaving(false)
    }
  }

  const customerName = customer
    ? [customer.fname, customer.lname].filter(Boolean).join(" ") || "-"
    : ""
  const customerAddress = customer
    ? [customer.address?.addressLine1, customer.address?.addressLine2, customer.pincode].filter(Boolean).join(", ")
    : ""

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered style={{ maxWidth: 680 }}>
      <ModalHeader toggle={toggle} tag="h4">
        {isEdit ? "Edit Subscription" : "Add Subscription"}
      </ModalHeader>
      <ModalBody>
        {isEdit && (loadingEdit || !editing) ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
        <Form onSubmit={handleSave} noValidate>
          {/* 1. Customer (locked when editing) */}
          <Label>Customer Mobile <span className="text-danger">*</span></Label>
          <div className="d-flex gap-2 mb-2">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Enter 10-digit mobile number"
              value={mobile}
              maxLength={10}
              disabled={isEdit}
              onChange={e => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  findCustomer()
                }
              }}
              invalid={!!lookupError}
            />
            <Button
              type="button"
              color="primary"
              onClick={findCustomer}
              disabled={finding || isEdit}
              className="text-nowrap"
            >
              {finding ? "Finding..." : "Get Customer"}
            </Button>
          </div>
          {lookupError && <div className="text-danger font-size-12 mb-2">{lookupError}</div>}

          {customer && (
            <div className="border rounded p-3 mb-3 bg-light">
              <Row className="font-size-13">
                <Col sm="6" className="mb-1"><span className="text-muted">Name:</span> <b>{customerName}</b></Col>
                <Col sm="6" className="mb-1"><span className="text-muted">Mobile:</span> {formatMobile(customer.mobile)}</Col>
                <Col sm="6" className="mb-1"><span className="text-muted">Email:</span> {customer.email || "-"}</Col>
                <Col sm="6" className="mb-1"><span className="text-muted">City:</span> {customer.city?.name || "-"}</Col>
                {customerAddress && (
                  <Col xs="12"><span className="text-muted">Address:</span> {customerAddress}</Col>
                )}
                {isEdit && (
                  <Col xs="12" className="mt-1">
                    <span className="text-muted">Started:</span> {formatDate(editing?.startDate)} ·{" "}
                    <span className="text-muted">Turns used:</span> {usedTurns}
                  </Col>
                )}
              </Row>
            </div>
          )}

          {!isEdit && activeSubscription && (
            <Alert color="warning" className="py-2">
              Already has an active <b>{activeSubscription.packageName}</b> plan till{" "}
              <b>{formatDate(activeSubscription.expiryDate)}</b>. A new subscription can be added after it ends.
            </Alert>
          )}

          {/* 2. Plan + payment */}
          <fieldset disabled={!isEdit && (!customer || !!activeSubscription)}>
            <Row>
              <Col md="12" className="mb-3">
                <Label>Package <span className="text-danger">*</span></Label>
                <Input type="select" name="packageId" value={form.packageId} onChange={handleChange}>
                  <option value="">Select package</option>
                  {packages.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.name} — {formatINR(p.price)}
                    </option>
                  ))}
                </Input>
                {selectedPackage && !isEdit && (
                  <div className="text-muted font-size-12 mt-1">
                    {selectedPackage.turns} turns · Starts today, valid till {formatDate(validTill)}
                  </div>
                )}
              </Col>

              <Col md="6" className="mb-3">
                <Label>Payment Mode {!isEdit && <span className="text-danger">*</span>}</Label>
                <Input type="select" name="paymentMode" value={form.paymentMode} onChange={handleChange}>
                  <option value="">{isEdit && !isManual ? "Online (Razorpay)" : "Select payment mode"}</option>
                  {PAYMENT_MODES.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Input>
              </Col>
              <Col md="6" className="mb-3">
                <Label>Amount Received (₹) <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  name="amount"
                  placeholder="0"
                  value={form.amount}
                  onChange={e => handleChange({ target: { name: "amount", value: e.target.value.replace(/\D/g, "") } })}
                />
              </Col>

              <Col md="6" className="mb-3">
                <Label>Reference / Transaction No.</Label>
                <Input
                  name="referenceNo"
                  placeholder="UPI ref, receipt no., etc. (optional)"
                  value={form.referenceNo}
                  onChange={handleChange}
                />
              </Col>
              <Col md="6" className="mb-3">
                <Label>Note</Label>
                <Input
                  name="note"
                  placeholder={isEdit ? "Optional" : "Why it was added manually (optional)"}
                  value={form.note}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          </fieldset>

          {isEdit && (
            <p className="text-muted font-size-12 mb-2">Orders already booked on this plan stay as they are.</p>
          )}

          <div className="text-end">
            <Button color="secondary" type="button" className="me-2" onClick={toggle}>
              Close
            </Button>
            <Button color="success" type="submit" disabled={!canSave}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Subscription"}
            </Button>
          </div>
        </Form>
        )}
      </ModalBody>
    </Modal>
  )
}

export default AddSubscriptionModal
